'use client';

import { BLOCKS } from '@contentful/rich-text-types';
import { cn, getColorway } from '@/utils/styles';
import { liveUpdateAttrs } from '@/libs/contentful/utils';

import CTA from '@/components/shared/CTA/CTA';
import Media from '@/components/shared/Media/Media';
import { CardsVerticalScrollItemMobileProps } from './types';
import RichTextHeadline from '@/components/shared/RichTextHeadline/RichTextHeadline';
import RichTextList from '@/components/shared/RichTextList/RichTextList';
import { getBulletListStyle } from '@/utils/bulletListHelpers';
import { useCollapsibleCopy } from './useCollapsibleCopy';
import { getHeaderImageSelection } from './headerImageSelection';
import { getMobileStickyOffset } from './stickyOffset';

const LIST_NODE_TYPES: ReadonlySet<string> = new Set([BLOCKS.UL_LIST, BLOCKS.OL_LIST]);

function hasListContent(copy: CardsVerticalScrollItemMobileProps['copy']): boolean {
  return copy?.json?.content?.some(node => LIST_NODE_TYPES.has(node.nodeType)) ?? false;
}

function getListItemCount(copy: CardsVerticalScrollItemMobileProps['copy']): number {
  if (!copy?.json?.content) return 0;

  return copy.json.content.reduce((total, node) => {
    if (!LIST_NODE_TYPES.has(node.nodeType)) return total;
    return total + (node.content?.filter(child => child.nodeType === BLOCKS.LIST_ITEM).length ?? 0);
  }, 0);
}

function getListClampId(sysId: string | undefined, index: number): string {
  const safeId = (sysId ?? `index-${index}`).replace(/[^a-zA-Z0-9_-]/g, '-');
  return `cards-vertical-scroll-list-${safeId}`;
}

const MobileCardsVerticalScrollItem = ({
  sys,
  headline,
  headerImage,
  headerImageOnLight,
  copy,
  desktopMedia,
  mobileMedia,
  desktopAspectRatio,
  mobileAspectRatio,
  cta,
  index,
  backgroundColor,
  bulletStyle,
  sectionName,
  mobileCollapsedLines,
  offsetMobileScrollTop,
  stickyTopOffset,
}: CardsVerticalScrollItemMobileProps) => {
  const activeHeaderImage = getHeaderImageSelection({
    backgroundColor,
    headerImageOnDark: headerImage,
    headerImageOnLight,
  });
  const isListCopy = hasListContent(copy);
  const listItemCount = isListCopy ? getListItemCount(copy) : 0;
  const listShouldTruncate = Boolean(mobileCollapsedLines && listItemCount > mobileCollapsedLines);
  const listClampId = getListClampId(sys?.id, index);

  const { copyRef, isExpanded, shouldTruncate, toggleExpanded } = useCollapsibleCopy({
    collapsedLines: mobileCollapsedLines,
    isListContent: isListCopy,
    initialShouldTruncate: isListCopy ? listShouldTruncate : undefined,
  });

  const isCollapsed = shouldTruncate && !isExpanded;
  const baseStickyOffset = stickyTopOffset ?? 'var(--height-nav)';
  const stickyOffset = getMobileStickyOffset({
    baseStickyOffset,
    index,
    offsetMobileScrollTop,
  });

  const copyStyle = (() => {
    if (!isCollapsed || !mobileCollapsedLines) return undefined;

    if (isListCopy) return undefined;

    return {
      display: '-webkit-box',
      WebkitLineClamp: mobileCollapsedLines,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
    };
  })();

  return (
    <div
      id={sectionName}
      className={cn(
        'relative w-full overflow-hidden',
        'rounded-lg',
        'shadow-[0_-10px_20px_rgba(0,0,0,0.25)]',
        getColorway(backgroundColor),
        'sticky overflow-hidden',
        index > 0 && '-mt-8',
        'p-6 pb-32',
      )}
      style={{
        zIndex: 10 + index,
        top: stickyOffset,
        scrollMarginTop: stickyOffset,
      }}
      data-testid={`cards-vertical-scroll-item-${index}`}>
      {isListCopy && isCollapsed && mobileCollapsedLines && (
        <style>{`#${listClampId} ul > li:nth-child(n+${mobileCollapsedLines + 1}), #${listClampId} ol > li:nth-child(n+${mobileCollapsedLines + 1}) { display: none; }`}</style>
      )}
      {activeHeaderImage.media ? (
        <div
          data-testid="headline"
          className="rounded-none min-h-12"
          {...liveUpdateAttrs(sys?.id, activeHeaderImage.fieldName)}>
          <Media media={activeHeaderImage.media} sizes="100vw" className="max-w-full rounded-none" />
        </div>
      ) : (
        <div
          data-testid="headline"
          className={cn('text-heading-lg w-full text-left pb-4')}
          {...liveUpdateAttrs(sys?.id, 'headline')}>
          <RichTextHeadline headline={headline} />
        </div>
      )}
      <div
        id={isListCopy ? listClampId : undefined}
        ref={copyRef}
        className={cn('font-serif text-label-serif [&_li]:my-0.5 mb-1.5')}
        style={copyStyle}
        {...liveUpdateAttrs(sys?.id, 'copy')}
        data-testid="card-expanded-copy">
        <RichTextList item={copy} listItemClasses="my-0" bulletListStyle={getBulletListStyle(bulletStyle)} />
      </div>
      {shouldTruncate && (
        <button
          type="button"
          onClick={toggleExpanded}
          className="cursor-pointer bg-transparent border-none p-0 mt-2 font-sans text-sm font-semibold underline mb-6"
          data-testid="see-more-toggle">
          {isExpanded ? 'See less' : 'See more'}
        </button>
      )}
      {cta && <CTA cta={cta} variant="outline" testId="card-expanded-cta" className={cn('max-w-1/2 mt-4 mb-8')} />}
      <Media
        media={mobileMedia ?? desktopMedia}
        sizes="100vw"
        aspectRatio={mobileAspectRatio ?? desktopAspectRatio}
        className={cn('w-full rounded-lg')}
      />
    </div>
  );
};

export default MobileCardsVerticalScrollItem;
